import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
export function HeadlightRestorationPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Headlight Restoration',
    provider: {
      '@type': 'LocalBusiness',
      name: "Mike's Pro Detail",
      telephone: '+18183819905'
    },
    areaServed: ['Ventura County', 'Camarillo', 'Ventura', 'Thousand Oaks'],
    description: 'Professional headlight restoration service to remove yellowing, oxidation, and fog, improving nighttime visibility and vehicle appearance.'
  };
  const faqs = [{
    question: 'How long does headlight restoration last?',
    answer: 'Because we apply a durable UV sealant as the final step, our headlight restoration lasts far longer than cheap DIY kits — typically a year or more, depending on sun exposure and care.'
  }, {
    question: 'Why do headlights turn yellow and cloudy?',
    answer: 'Headlight lenses are made of polycarbonate plastic with a factory UV clear coat. Over time that coating breaks down from sun and heat, causing the plastic underneath to oxidize and turn yellow or foggy.'
  }, {
    question: 'Is headlight restoration worth it vs. replacement?',
    answer: 'Yes. Replacing headlight assemblies can cost hundreds or thousands of dollars. Restoration delivers clear, like-new lenses for a fraction of the cost.'
  }, {
    question: 'Does it improve nighttime visibility?',
    answer: 'Significantly. Clear lenses let your bulbs project light properly, improving your vision at night and in poor weather and keeping you safer on the road.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/services'
  }, {
    name: 'Headlight Restoration',
    item: 'https://www.fulldetailpro.com/services/headlight-restoration'
  }];
  return <ServicePageLayout title="Headlight Restoration Ventura County" subtitle="Improve your nighttime visibility and enhance your car's appearance by permanently removing yellowing, oxidation, and fog from your headlights." metaDescription="Mobile headlight restoration in Ventura County, including Camarillo, Ventura and Thousand Oaks. Remove yellowing and oxidation for clearer headlights. Call (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-905" data-mp="2134hfvq33mt">
      <p data-id="element-906" data-mp="3fdmzidm7n2n6">
        Are your vehicle's headlights looking cloudy, yellowed, or foggy? Not
        only does this age the appearance of your car significantly, but it also
        poses a serious safety hazard by drastically reducing your nighttime
        visibility. At Mike's Pro Detail, our professional{' '}
        <strong data-id="element-907" data-mp="2wozufv41ad9l">Headlight Restoration</strong> service permanently removes heavy
        oxidation and restores crystal-clear clarity to your lenses. We provide
        this essential mobile service throughout Camarillo CA, Thousand Oaks,
        and the greater Ventura County area.
      </p>

      <h2 data-id="element-908" data-mp="1erdb4i3bb3nx">Why Do Headlights Turn Yellow?</h2>
      <p data-id="element-909" data-mp="1esenqqzz7y7r">
        Modern vehicle headlights are made of polycarbonate plastic, which is
        lightweight and impact-resistant. However, this plastic is highly
        susceptible to UV degradation from the sun. Manufacturers apply a
        UV-protective clear coat at the factory, but over time, this coating
        breaks down due to sun exposure, heat from the bulbs, and harsh cleaning
        chemicals. Once the coating fails, the plastic oxidizes rapidly,
        resulting in that cloudy, yellow appearance.
      </p>

      <h2 data-id="element-910" data-mp="r20vazdhbxcn">Our Professional Restoration Process</h2>
      <p data-id="element-911" data-mp="m3h3wjg49tcw">
        Don't be fooled by cheap DIY kits or "wipe-on" fixes that only last a
        few weeks. We perform a true, multi-step mechanical restoration to
        permanently fix the issue:
      </p>

      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-912" data-mp="c5jhm1hti6mx">
        <li className="flex items-start" data-id="element-913" data-mp="c6umy5qwvmg5">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-914" data-mp="2c7srzq8o0b6e" />
          <span data-id="element-915" data-mp="2h5kvziml3nqy">
            <strong data-id="element-916" data-mp="2h642822vgnp5">Taping & Protection:</strong> We carefully tape off the
            surrounding paint and trim to ensure no damage occurs during the
            sanding process.
          </span>
        </li>
        <li className="flex items-start" data-id="element-917" data-mp="27ezmv19ab0ts">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-918" data-mp="27dy9gq4sq28w" />
          <span data-id="element-919" data-mp="1tfgtxfp1nw22">
            <strong data-id="element-920" data-mp="1teynjq8o41gf">Wet Sanding:</strong> We use progressive grits of
            specialized sandpaper to completely strip away the failed factory
            clear coat and the oxidized plastic layer.
          </span>
        </li>
        <li className="flex items-start" data-id="element-921" data-mp="1oh6xz2vhwrfe">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-922" data-mp="1ohygpd3zuuo9" />
          <span data-id="element-923" data-mp="3dvlhn8rnpo41">
            <strong data-id="element-924" data-mp="3dx6amtyboyzs">Machine Compounding:</strong> Using a machine polisher and
            heavy cutting compound, we remove the fine sanding marks and begin
            restoring clarity.
          </span>
        </li>
        <li className="flex items-start" data-id="element-925" data-mp="178mdjhgjldzt">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-926" data-mp="128rqjozw18k1" />
          <span data-id="element-927" data-mp="12akw10yrlmqg">
            <strong data-id="element-928" data-mp="126x4g7cvraed">Machine Polishing:</strong> A refining polish is used to
            bring the polycarbonate lens back to a flawless, glass-like
            transparency.
          </span>
        </li>
        <li className="flex items-start" data-id="element-929" data-mp="1c54gcbsbb29i">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-930" data-mp="1pxaxa03ahda0" />
          <span data-id="element-931" data-mp="1lx0roojesovo">
            <strong data-id="element-932" data-mp="1lximdgqfav1h">UV Protection Application:</strong> This is the most
            critical step. We apply a durable specialized UV
            sealant to protect the bare plastic and prevent it from yellowing
            again.
          </span>
        </li>
      </ul>

      <h2 data-id="element-933" data-mp="5ihdc6u4gsz5">Safety and Aesthetics</h2>
      <p data-id="element-934" data-mp="5hpjb0g6b3az">
        Restoring your headlights is one of the most cost-effective ways to
        instantly improve the look of your vehicle. More importantly, clear
        headlights allow your bulbs to project light properly, significantly
        improving your vision at night and during inclement weather, keeping you
        and your family safe on the road.
      </p>

      <h2 data-id="element-935" data-mp="29mlngvn952ua">Convenient Mobile Service</h2>
      <p data-id="element-936" data-mp="29lkcssa5eid3">
        Replacing headlight assemblies can cost hundreds or even thousands of
        dollars. Our restoration service is a fraction of the cost and yields
        incredible results. If you are searching for "Mobile Detailing Ventura
        County" or "Car Detailing Camarillo" to fix your cloudy headlights,
        Mike's Pro Detail will come directly to your home or workplace to
        perform the restoration.
      </p>

      <h2 data-id="element-937" data-mp="2tcp24ofpt8zz">Related Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-938" data-mp="2xc67ziz2085o">
        <Link to="/full-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-939" data-mp="32b9ez8ylgoga">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/exterior-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-940" data-mp="3hetzcfo1zf43">
          Exterior Detailing &rarr;
        </Link>
        <Link to="/services/engine-bay-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-941" data-mp="33eja1h2e68l1">
          Engine Bay Detailing &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-942" data-mp="3v67frkqnzwuw">
          Mobile Detailing &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}